// Conflict resolution for concurrent edits to one board.
//
// Every Excalidraw element carries `version` (a monotonic counter bumped on each
// local mutation) and `versionNonce` (a random tiebreak). This module implements
// the same comparison Excalidraw's own `reconcileElements` uses on the client, so
// the server and every client converge on the same scene no matter what order
// updates arrive in.
//
// The rule, in one sentence: the higher `version` wins, and if the versions are
// equal the LOWER `versionNonce` wins.
//
// Everything here is pure. Nothing mutates its arguments.

import crypto from "node:crypto";

/**
 * Should `incoming` replace `existing`?
 * @returns {boolean} true when the incoming element is the newer one.
 */
export const incomingWins = (existing, incoming) => {
  if (!existing) return true;
  if (!incoming) return false;
  const ev = existing.version ?? 0;
  const iv = incoming.version ?? 0;
  if (iv !== ev) return iv > ev;
  // Same version from two clients: break deterministically on the nonce so both
  // sides pick the same winner without another round trip.
  const en = existing.versionNonce ?? 0;
  const inc = incoming.versionNonce ?? 0;
  if (inc !== en) return inc < en;
  return false;
};

/**
 * Fold a batch of incoming elements into a board's element map.
 *
 * @param {Map<string, object>} current  board state, not mutated
 * @param {object[]} incoming            elements from a client or an agent
 * @returns {{ elements: Map<string, object>, accepted: object[] }}
 *          the new map, and only the elements that actually won (which is what
 *          gets broadcast, so a rejected stale update does not echo around)
 */
export const applyUpdates = (current, incoming) => {
  const elements = new Map(current);
  const accepted = [];
  for (const el of incoming) {
    if (!el || typeof el.id !== "string") continue;
    if (incomingWins(elements.get(el.id), el)) {
      elements.set(el.id, el);
      accepted.push(el);
    }
  }
  return { elements, accepted };
};

const nonce = () => crypto.randomInt(0, 2 ** 31);

/**
 * Raise freshly compiled elements above whatever is already on the board.
 *
 * Compiled elements always come out at `version: 1`. Writing them into a live
 * board as-is would lose every reconciliation against an element that a human
 * has already touched, so an agent's build/patch would silently do nothing. This
 * lifts each element to one past the incumbent and re-randomises the nonce.
 *
 * @param {object[]} elements
 * @param {Map<string, object>} existing
 */
export const promoteVersions = (elements, existing) =>
  elements.map((el) => {
    const prev = existing.get(el.id);
    const floor = Math.max(prev?.version ?? 0, el.version ?? 0);
    return { ...el, version: floor + 1, versionNonce: nonce(), updated: Date.now() };
  });

/**
 * Mark elements deleted the way Excalidraw does: keep the tombstone so the
 * deletion itself replicates, rather than dropping the element and letting a
 * peer resurrect it.
 */
export const tombstone = (elements, ids) => {
  const gone = new Set(ids);
  return elements.map((el) =>
    gone.has(el.id) && !el.isDeleted
      ? { ...el, isDeleted: true, version: (el.version ?? 0) + 1, versionNonce: nonce(), updated: Date.now() }
      : el,
  );
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Drop tombstones older than `maxAgeMs` so a long-lived board does not grow
 * without bound. Anything still within the window stays, because a client that
 * was offline needs to learn about the deletion when it comes back.
 */
export const sweepDeleted = (elements, now = Date.now(), maxAgeMs = DAY_MS) =>
  elements.filter((el) => !el.isDeleted || now - (el.updated ?? 0) < maxAgeMs);

/**
 * A cheap fingerprint of a scene, used to skip persisting when nothing changed.
 * Mirrors Excalidraw's own `getSceneVersion`: the sum of element versions.
 */
export const sceneVersion = (elements) =>
  elements.reduce((sum, el) => sum + (el.version ?? 0), 0);
